import MainLayout from "@core/layouts/mainLayout.vue";
import AppEnvService from "@core/services/utils/appEnvService";

import talModule from "@tal/router/talRoutes";
import sosModule from "@sos/router/sosRoutes";
import rmsModule from "@rms/router/rmsRoutes";
import modularModule from "@modular/router/modularRoutes";
import pronovaModule from "@pronova/router/pronovaRoutes";
import pasModule from "@pas/router/pasRoutes";

/**
 * Module registry — each remote owns its routes manifest.
 *
 * Adding a new module = creating remote-x/router/xRoutes.js and adding one entry here.
 * Removing a module = deleting that entry. The shell never references module pages directly.
 *
 * Aliases : brand-name variants used in CI/.env (talent, skilloscope) map to the
 * canonical short code (tal, sos). This lets the deployment naming stay consistent
 * with the brand while the routes manifest keeps its short identifier.
 */
const MODULES = {
  tal: talModule,
  talent: talModule,           // alias for APP_CODE=talent (.env.talent)
  sos: sosModule,
  skilloscope: sosModule,      // alias for APP_CODE=skilloscope (.env.skilloscope)
  rms: rmsModule,
  modular: modularModule,
  pronova: pronovaModule,
  pas: pasModule,
};

/**
 * APP_CODE is deployment-time (set via .env files into window.environment).
 * It determines which module is "active" — i.e. whose routes are mounted under main-layout.
 */
const getAppCode = () => window.environment?.APP_CODE || 'tal';

export const getActiveModule = () => MODULES[getAppCode()] || MODULES.tal;

/**
 * Unique set of module references (alias keys point to the same module object,
 * we deduplicate by identity to avoid mounting the same route table twice).
 */
const uniqueModules = () => [...new Set(Object.values(MODULES))];

/**
 * Returns the routes to mount:
 *  - all routes from the active module (selected by APP_CODE)
 *  - plus all routes from modules flagged `alwaysMounted: true` (transverse, e.g. marketing site)
 *
 * Inactive modules without the flag are not mounted — their pages never reach the route table.
 *
 * Dev mode exception: ALL modules are mounted so developers can navigate to any
 * portal (e.g. /tal-portal, /sos-portal, /rms-portal) without changing APP_CODE
 * or rebuilding. This branch is statically eliminated in production builds
 * (`import.meta.env.DEV` is replaced by the literal `false` by Vite).
 */
const getMountedRoutes = () => {
  if (import.meta.env.DEV) {
    return uniqueModules().flatMap(m => m.routes);
  }
  const active = getActiveModule();
  const extras = uniqueModules().filter(m => m.alwaysMounted && m.code !== active.code);
  return [active, ...extras].flatMap(m => m.routes);
};

/**
 * Landing page resolution.
 *
 * APP_LANDING_PAGE is a DB AppSetting (string URL path). Evaluated dynamically
 * on each navigation to '/' so a hot config change takes effect without rebuild.
 * Fallback when unset → /no-landing (generic placeholder page).
 */
export const resolveLandingRedirect = () => AppEnvService.getEnv('APP_LANDING_PAGE') || '/no-landing';

const commonRoutes = [
  {
    // Module Raccourcis (épinglés + récents), ouvert depuis l'entrée fixe du
    // tiroir (mainLayout) — statique, hors app_menu, commun à toutes les marques.
    path: '/shortcuts',
    name: 'app-shortcuts',
    component: () => import('@core/pages/appShortcuts.vue')
  },
  {
    // Journal d'activité de l'utilisateur connecté (lien depuis Raccourcis).
    path: '/activity',
    name: 'app-my-activity',
    component: () => import('@core/pages/appMyActivity.vue')
  },
  {
    path: '/no-landing',
    name: 'no-landing',
    component: () => import('@core/pages/noLanding.vue'),
    meta: { public: true }
  },
  {
    path: '/error401',
    name: 'error-401',
    component: () => import('@/pages/Error401.vue'),
    meta: { public: true }
  },
  {
    path: '/error-not-found',
    name: 'error-not-found',
    component: () => import('@/pages/ErrorNotFound.vue'),
    meta: { public: true }
  },
  {
    // Catch-all renders the loading page (URL preserved) — the dynamic routes
    // registered by routerService.loadMenu() may not exist yet at initial route
    // resolution time. Once they are added, applyRoutes() re-resolves and either
    // navigates to the real route or redirects to /error-not-found.
    path: '/:catchAll(.*)*',
    name: 'not-found',
    component: () => import('@/pages/Loading.vue'),
    meta: { public: true }
  }
];

export const buildRoutes = () => {
  const active = getActiveModule();
  const mountedRoutes = getMountedRoutes();
  if (import.meta.env.DEV) {
    console.log(`[Routes] Dev mode — all modules mounted: ${uniqueModules().map(m => m.code).join(',')} | active: ${active.code}`);
  } else {
    const extras = uniqueModules().filter(m => m.alwaysMounted && m.code !== active.code).map(m => m.code);
    console.log(`[Routes] Active module: ${active.code}${extras.length ? ` | always-mounted: ${extras.join(',')}` : ''}`);
  }

  return [
    {
      path: "/share/:code?",
      name: "ace-share",
      component: () => import('@ace/pages/ace/aceSharePortal.vue'),
      meta: { public: true }
    },
    {
      path: "/",
      name: "main-layout",
      component: MainLayout,
      children: [
        {
          path: "",
          name: "portal",
          redirect: resolveLandingRedirect
        },
        ...mountedRoutes,
        ...commonRoutes
      ]
    }
  ];
};

export default buildRoutes();
